/**
 * "Fehlende Eingaben" — the checklist the brief asks for by name.
 *
 * A null field is not a blank row somewhere in a long table; it is an item on a
 * list that says three things: what is missing, why it is missing (the
 * extractor's own sentence — "lieber leer als geraten"), and **which rule IDs
 * could not be computed because of it**. That last part is what turns a gap
 * from an annoyance into a decision: an architect can see that filling one
 * number unblocks a named check, and skip the ones that block nothing.
 *
 * Every entry carries a manual-entry affordance. Two shapes:
 *  · the value has a row in the table below → jump to it and open its editor
 *    (one editor per field, not two that can disagree);
 *  · the value has no row at all — the extractor never produced the key, e.g.
 *    `values.waldabstand_m` — → an inline editor right here, because there is
 *    nowhere else to put it.
 */
import { useId, useState } from 'react'
import { CornerDownRight, ListChecks } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { Button } from '@/components/ui/button'
import { useI18n } from '@/i18n'
import { parseInput } from '@/features/facts/factEditors'
import type { FactValue, MissingInput } from '@/api/types'

/** Blocks a named check — the only distinction this list really has to make. */
function isBlocking(item: MissingInput): boolean {
  return (item.blocks ?? []).length > 0
}

export function MissingInputsChecklist({
  items,
  knownPaths,
  busy,
  onJump,
  onSave,
}: {
  items: readonly MissingInput[]
  /** Paths that have a row in the facts table — those jump instead of editing here. */
  knownPaths: ReadonlySet<string>
  busy: boolean
  onJump: (path: string) => void
  onSave: (path: string, value: FactValue) => Promise<void> | void
}) {
  const { t } = useI18n()

  if (items.length === 0) {
    return (
      <p
        data-testid="missing-inputs-empty"
        className="rounded-sm border border-border bg-surface px-4 py-3 text-[13px] text-muted"
      >
        {t('facts.missingEmpty')}
      </p>
    )
  }

  // 9 of the Egg demo's 13 entries block nothing, and they stood ABOVE the 4
  // that do. The reader scrolled past nine "blockiert derzeit keine Regel" to
  // reach the four that are the actual work. Blocking first, the rest behind a
  // disclosure — still all thirteen, still every reason and every editor, just
  // no longer in front of the answer.
  const blocking = items.filter(isBlocking)
  const rest = items.filter((item) => !isBlocking(item))

  const row = (item: MissingInput) => (
    <MissingRow
      key={item.path}
      item={item}
      hasRow={knownPaths.has(item.path)}
      busy={busy}
      onJump={onJump}
      onSave={onSave}
    />
  )

  return (
    <div data-testid="missing-inputs" className="flex flex-col gap-4">
      {blocking.length > 0 ? (
        <ul data-testid="missing-inputs-blocking" className="flex flex-col gap-3">
          {blocking.map(row)}
        </ul>
      ) : (
        <p data-testid="missing-inputs-none-blocking" className="text-[13px] text-muted">
          {t('facts.missingNoneBlocking')}
        </p>
      )}

      {rest.length > 0 ? (
        <details data-testid="missing-inputs-rest" className="flex flex-col gap-3">
          <summary className="w-fit cursor-pointer rounded-xs text-[13px] font-medium text-ink-2 underline underline-offset-2">
            {t(rest.length === 1 ? 'facts.missingRestOne' : 'facts.missingRest', {
              count: rest.length,
            })}
          </summary>
          <p className="mt-2 max-w-[80ch] text-[12px] text-muted">{t('facts.missingRestLead')}</p>
          <ul className="mt-3 flex flex-col gap-3">{rest.map(row)}</ul>
        </details>
      ) : null}
    </div>
  )
}

function MissingRow({
  item,
  hasRow,
  busy,
  onJump,
  onSave,
}: {
  item: MissingInput
  hasRow: boolean
  busy: boolean
  onJump: (path: string) => void
  onSave: (path: string, value: FactValue) => Promise<void> | void
}) {
  const { t } = useI18n()
  const [draft, setDraft] = useState('')
  const [open, setOpen] = useState(false)
  const inputId = useId()
  const blocks = item.blocks ?? []

  return (
    <li
      data-testid="missing-input"
      data-path={item.path}
      className="flex flex-col gap-2 rounded-sm border border-border bg-white p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <Icon icon={ListChecks} className="mt-0.5 text-signal-amber-text" />
          <div className="flex min-w-0 flex-col">
            <span className="text-[14px] font-medium text-ink">{item.label_de}</span>
            <span className="num text-[11px] text-muted-2">{item.path}</span>
          </div>
        </div>
        {hasRow ? (
          <Button size="sm" variant="secondary" onClick={() => onJump(item.path)}>
            <Icon icon={CornerDownRight} />
            {t('facts.jumpToField')}
          </Button>
        ) : (
          <Button size="sm" onClick={() => setOpen((value) => !value)} disabled={busy}>
            {t('facts.enter')}
          </Button>
        )}
      </div>

      {/* The extractor's own reason, verbatim. */}
      <p className="text-[13px] text-muted">{item.reason}</p>

      {/* Which named checks this gap blocks — the reason the list is actionable. */}
      {blocks.length > 0 ? (
        <p data-testid="missing-blocks" className="flex flex-wrap items-center gap-2 text-[12px]">
          <span className="text-muted">{t('facts.blocks')}:</span>
          {blocks.map((ruleId) => (
            <span
              key={ruleId}
              data-rule={ruleId}
              className="num rounded-xs border border-border bg-surface px-1.5 py-0.5 text-ink-2"
            >
              {ruleId}
            </span>
          ))}
        </p>
      ) : (
        <p data-testid="missing-blocks-none" className="text-[12px] text-muted">
          {t('facts.blocksNone')}
        </p>
      )}

      {open && !hasRow ? (
        <form
          data-testid="missing-editor"
          className="flex flex-wrap items-end gap-3 rounded-sm border border-border-strong bg-surface px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault()
            const kind = item.path.startsWith('values.') || item.path.startsWith('form.')
              ? 'number'
              : 'text'
            void Promise.resolve(onSave(item.path, parseInput(kind, draft))).then(() =>
              setOpen(false),
            )
          }}
        >
          <div className="flex min-w-[14rem] flex-col gap-1">
            <label htmlFor={inputId} className="text-[12px] font-medium text-ink-2">
              {item.label_de}
            </label>
            <input
              id={inputId}
              value={draft}
              inputMode="decimal"
              onChange={(event) => setDraft(event.target.value)}
              className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
            />
          </div>
          <Button size="sm" type="submit" disabled={busy}>
            {t('facts.save')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
            {t('common.cancel')}
          </Button>
        </form>
      ) : null}
    </li>
  )
}
